import {useLayoutEffect, useRef, useState} from "react";
import ReCAPTCHA from "react-google-recaptcha";
import emailjs from "@emailjs/browser";
// import {useTheme} from "../../../hooks/useTheme";
import "./ContactSection.css";

import logoLight from "../../../assets/contacts-imgs/logo-light.svg";
import logoDark from "../../../assets/contacts-imgs/logo-dark.svg";
import mailLight from "../../../assets/contacts-imgs/mail-light.svg";
import mailDark from "../../../assets/contacts-imgs/mail-dark.svg";
import linkedinLight from "../../../assets/contacts-imgs/in-light.svg";
import linkedinDark from "../../../assets/contacts-imgs/in-dark.svg";
// декоративний чекбокс закоментовано (лишили лише справжній Google-віджет):
// import captchaIcon from "../../../assets/contacts-imgs/captcha.svg";

import {
  ReactComponent as ContactBlockFrameLight
} from "../../../assets/contacts-imgs/block_contact_form-light-original.svg";
import {
  ReactComponent as ContactBlockFrameDark
} from "../../../assets/contacts-imgs/block_contact_form-dark-original.svg";

import {ReactComponent as InputFrameLightIcon} from "../../../assets/contacts-imgs/input-frame-light.svg";
import {ReactComponent as InputFrameDarkIcon} from "../../../assets/contacts-imgs/input-frame-dark.svg";
// Purpose-built for the textarea's own aspect ratio (458x162, vs the plain
// input frame's 458x54) -- used only for the Message field's normal state,
// so its corners stay crisp instead of being stretched ~3x taller like the
// generic input frame would.
import {
  ReactComponent as InputFrameLightTextAreaIcon
} from "../../../assets/contacts-imgs/input-frame-light-text-area.svg";
import {
  ReactComponent as InputFrameDarkTextAreaIcon
} from "../../../assets/contacts-imgs/input-frame-dark-text-area.svg";
import {ReactComponent as InputErrorFrameLightIcon} from "../../../assets/contacts-imgs/input-error-frame.svg";
import {ReactComponent as InputErrorFrameDarkIcon} from "../../../assets/contacts-imgs/input-error-frame-dark.svg";
// Same recolor as input-error-frame(-dark).svg vs input-frame-light/dark.svg
// (background fill kept, wobble-outline fill swapped to #F55D60) applied to
// the text-area-specific frames instead of the generic 458x54 one, so the
// Message field's error state keeps the same crisp, undistorted corners as
// its normal state.
import {
  ReactComponent as InputErrorFrameTextAreaIcon
} from "../../../assets/contacts-imgs/input-error-frame-text-area.svg";
import {
  ReactComponent as InputErrorFrameDarkTextAreaIcon
} from "../../../assets/contacts-imgs/input-error-frame-dark-text-area.svg";
// декоративний чекбокс закоментовано (лишили лише справжній Google-віджет):
// import {ReactComponent as CheckboxFrameIcon} from "../../../assets/contacts-imgs/input-frame-cream.svg";
// import {ReactComponent as CheckboxErrorFrameIcon} from "../../../assets/contacts-imgs/input-error-frame.svg";


// Same inline-SVG-card technique as Header's NavButtonLightIcon: an <input>/
// <textarea> can't take child elements the way a <button> or <p> can, so the
// frame art renders in a wrapping div instead, absolutely positioned behind
// the field (z-index: -1) while the field itself stays transparent on top.
function ContactInputFrame({
                             error,
                             children,
                             LightIcon = InputFrameLightIcon,
                             DarkIcon = InputFrameDarkIcon,
                             ErrorLightIcon = InputErrorFrameLightIcon,
                             ErrorDarkIcon = InputErrorFrameDarkIcon,
                           }) {
  return (
    <div className={`contact-inputWrap${error ? " contact-inputWrap--error" : ""}`}>
      <LightIcon
        className="contact-input-bg contact-input-bg-light"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      />
      <DarkIcon
        className="contact-input-bg contact-input-bg-dark"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      />
      <ErrorLightIcon
        className="contact-input-bg contact-input-bg-error-light"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      />
      <ErrorDarkIcon
        className="contact-input-bg contact-input-bg-error-dark"
        preserveAspectRatio="none"
        aria-hidden="true"
        focusable="false"
      />
      {children}
    </div>
  );
}


const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const NAME_RE = /^[\p{L}][\p{L}'-]*(?:\s[\p{L}'-]+)*$/u;
const MESSAGE_MAX_LENGTH = 120;
const MESSAGE_MIN_LENGTH = 30;
const INITIAL_FORM = {name: "", company: "", email: "", contact: "", message: ""};


const RECAPTCHA_SITE_KEY = "6LfNY8wtAAAAAFTWFaR8Z7TRQCCryRjT8oVnbbaV";
const EMAILJS_SERVICE_ID = "service_kckg07y";
const EMAILJS_TEMPLATE_ID = "template_8ltfc1r";
const EMAILJS_PUBLIC_KEY = "1XOhcvurtEwuIutO3";
const SEND_PHP_URL = `${process.env.PUBLIC_URL}/serverUtils/send.php`;

// Google рендерить чекбокс-віджет з фіксованим розміром 304x78 -- жодного
// props для ширини немає. Щоб він займав всю ширину полів форми (а не лише
// не вилазив за них), масштабуємо динамічно через ResizeObserver замість
// підбору transform:scale() на кожному брейкпойнті окремо -- так ширина
// завжди точно збігається з .contact-input, а не лише "влазить" з запасом.
const RECAPTCHA_WIDGET_WIDTH = 304;
const RECAPTCHA_WIDGET_HEIGHT = 72;


export default function ContactSection() {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [captchaToken, setCaptchaToken] = useState(null);
  const [recaptchaScale, setRecaptchaScale] = useState(1);
  const recaptchaRef = useRef(null);
  const recaptchaWrapRef = useRef(null);

  useLayoutEffect(() => {
    const wrap = recaptchaWrapRef.current;
    if (!wrap) return;

    const updateScale = () => {
      setRecaptchaScale(wrap.clientWidth / RECAPTCHA_WIDGET_WIDTH);
    };

    updateScale();
    const ro = new ResizeObserver(updateScale);
    ro.observe(wrap);
    return () => ro.disconnect();
  }, []);

  const nameTrimmed = form.name.trim();
  const nameValid = nameTrimmed.length >= 2 && NAME_RE.test(nameTrimmed);
  const emailValid = EMAIL_RE.test(form.email);
  // Teams/Telegram необов'язкове: порожнє -- ок, заповнене -- перевіряємо формат.
  const contactTrimmed = form.contact.trim();
  const contactValid = contactTrimmed.length === 0
    || (contactTrimmed.length >= 3 && /[\p{L}\d]/u.test(contactTrimmed));
  const messageTrimmed = form.message.trim();
  const messageValid = messageTrimmed.length >= MESSAGE_MIN_LENGTH && messageTrimmed.length <= MESSAGE_MAX_LENGTH;
  const messageError = messageTrimmed.length < MESSAGE_MIN_LENGTH
    ? `Message must be ${MESSAGE_MIN_LENGTH} characters or more`
    : `Message must be ${MESSAGE_MAX_LENGTH} characters or fewer`;
  const formValid = nameValid && emailValid && contactValid && messageValid && captchaToken;


  const handleChange = (field) => (e) => {
    setForm(prev => ({...prev, [field]: e.target.value}));
  }


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitted(true);

    if (!formValid) return;

    setStatus("sending");

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          from_name: form.name,
          from_email: form.email,
          company: form.company,
          telegram_teams: form.contact,
          message: form.message,
          reply_to: form.email,
          "g-recaptcha-response": captchaToken,
        },
        EMAILJS_PUBLIC_KEY
      );

      setStatus("success");

      // Дублюємо заявку в send.php (ClickUp-таска + indexesleads.com).
      // Не чекаємо і не показуємо помилку користувачу: лист уже пішов через
      // EmailJS, і статус error спровокував би повторну відправку (дубль листа).
      // FormData, бо send.php читає поля з $_POST; імена полів -- як у send.php
      // (social, а не contact).
      const leadData = new FormData();
      leadData.append("name", form.name);
      leadData.append("company", form.company);
      leadData.append("email", form.email);
      leadData.append("social", form.contact);
      leadData.append("message", form.message);
      fetch(SEND_PHP_URL, {method: "POST", body: leadData})
        .then(res => {
          if (!res.ok) throw new Error(`send.php responded ${res.status}`);
        })
        .catch(err => console.error("send.php request failed:", err));

      setForm(INITIAL_FORM);
      setCaptchaToken(null);
      setSubmitted(false);
    } catch (err) {
      console.error("Contact form submit failed:", err, err?.status, err?.text);
      setStatus("error");
    } finally {
      recaptchaRef.current?.reset();
    }
  };


  return (
    <section className="contact">
      <div className="contact-info">
        <img className="contact-logo contact-logo-light" src={logoLight} alt="Vassabi"/>
        <img className="contact-logo contact-logo-dark" src={logoDark} alt="Vassabi"/>

        <div className="contact-copy">
          <p className="contact-text">
            We are always happy to establish connections with new partners, such as mobile
            aggregators, publishers, and advertising networks.
          </p>
          <p className="contact-text">Please contact us to start cooperation!</p>
        </div>

        <div className="contact-linkGroups">
          <div className="contact-linkGroup">
            <h3 className="contact-linkTitle">Follow us at:</h3>
            <a className="contact-link"
               href="https://www.linkedin.com/company/vassabi"
               target="_blank"
               rel="noreferrer">
              <img className="contact-icon contact-icon-light" src={linkedinLight} alt=""/>
              <img className="contact-icon contact-icon-dark" src={linkedinDark} alt=""/>
              Linkedin
            </a>
          </div>

          <div className="contact-linkGroup">
            <h3 className="contact-linkTitle">Write us directly:</h3>
            <a className="contact-link" href="mailto:info@vassabi.com">
              <img className="contact-icon contact-icon-light" src={mailLight} alt=""/>
              <img className="contact-icon contact-icon-dark" src={mailDark} alt=""/>
              info@vassabi.com
            </a>
          </div>
        </div>
      </div>

      <form className="contact-form" onSubmit={handleSubmit} noValidate>
        <ContactBlockFrameLight
          className="contact-form-bg contact-form-bg-light"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        />
        <ContactBlockFrameDark
          className="contact-form-bg contact-form-bg-dark"
          preserveAspectRatio="none"
          aria-hidden="true"
          focusable="false"
        />

        <div className="contact-field">
          {submitted && !nameValid && (
            <span className="contact-error">Please enter a valid name</span>
          )}
          <ContactInputFrame error={submitted && !nameValid}>
            <input
              className="contact-input"
              type="text"
              placeholder="Your name"
              value={form.name}
              onChange={handleChange("name")}
              required
              aria-invalid={submitted && !nameValid}
            />
          </ContactInputFrame>
        </div>

        <div className="contact-field">
          <ContactInputFrame>
            <input
              className="contact-input"
              type="text"
              placeholder="Company"
              value={form.company}
              onChange={handleChange("company")}
            />
          </ContactInputFrame>
        </div>

        <div className="contact-field">
          {submitted && !emailValid && (
            <span className="contact-error">Invalid email address</span>
          )}
          <ContactInputFrame error={submitted && !emailValid}>
            <input
              className="contact-input"
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={handleChange("email")}
              required
              aria-invalid={submitted && !emailValid}
            />
          </ContactInputFrame>
        </div>

        <div className="contact-field">
          {submitted && !contactValid && (
            <span className="contact-error">Please enter a valid Teams/Telegram</span>
          )}
          <ContactInputFrame error={submitted && !contactValid}>
            <input
              className="contact-input"
              type="text"
              placeholder="Teams/Telegram"
              value={form.contact}
              onChange={handleChange("contact")}
              aria-invalid={submitted && !contactValid}
            />
          </ContactInputFrame>
        </div>

        <div className="contact-field">
          {submitted && !messageValid && (
            <span className="contact-error">{messageError}</span>
          )}
          <ContactInputFrame
            error={submitted && !messageValid}
            LightIcon={InputFrameLightTextAreaIcon}
            DarkIcon={InputFrameDarkTextAreaIcon}
            ErrorLightIcon={InputErrorFrameTextAreaIcon}
            ErrorDarkIcon={InputErrorFrameDarkTextAreaIcon}
          >
            <textarea
              className="contact-input contact-textarea"
              placeholder="Message"
              value={form.message}
              onChange={handleChange("message")}
              required
              aria-invalid={submitted && !messageValid}
            />
          </ContactInputFrame>
          <span
            className={`contact-charCount ${form.message.length > MESSAGE_MAX_LENGTH ?
              "contact-charCount--limit" : ""}`}>
            {form.message.length}/{MESSAGE_MAX_LENGTH}
          </span>
        </div>

        {/* Видимий віджет Google, як в avsabi/src/sections/Contact/Contact.jsx --
            це справжній чекбокс Google (не намальований), окремий від
            декоративного .contact-checkbox вище. Стилізувати сам віджет
            не можна (кросс-origin iframe), тому замість цього масштабуємо
            фіксовані 304x78 через recaptchaScale (порахований у useEffect
            вище від реальної ширини .contact-recaptchaWidget), щоб він
            займав рівно ту саму ширину, що й .contact-input. */}
        {submitted && !captchaToken && (
          <span className="contact-error contact-error--center">Please complete the reCAPTCHA</span>
        )}
        <div className="contact-recaptchaWidget" ref={recaptchaWrapRef}
             style={{height: RECAPTCHA_WIDGET_HEIGHT * recaptchaScale}}>
          <div
            className="contact-recaptchaScaler"
            style={{
              width: RECAPTCHA_WIDGET_WIDTH,
              height: RECAPTCHA_WIDGET_HEIGHT,
              transform: `scale(${recaptchaScale})`,
            }}
          >
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={RECAPTCHA_SITE_KEY}
              onChange={(token) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              onError={() => setCaptchaToken(null)}
            />
          </div>
        </div>

        {status === "success" && (
          <p className="contact-status contact-status--success">
            Message sent! We'll get back to you soon.
          </p>
        )}
        {status === "error" && (
          <p className="contact-status contact-status--error">
            Something went wrong. Please try again or email us at info@vassabi.com
          </p>
        )}

        <button type="submit"
                className="contact-submit"
                disabled={status === "sending"}>
          {status === "sending" ? "Sending…" : "Contact"}
        </button>
      </form>
    </section>
  );
}
